import React, { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TOptionValue } from "../option/optionsUtils";
import "./Dropdown.scss";
import { IDropdownItem } from "./dropdownUtils";
import NestedDropdown from "./NestedDropdown";

interface Props {
    items: IDropdownItem[];
    itemTranslation?: string;
    maxHeight?: number; // px
    onChange?: (item: IDropdownItem) => void;
    trigger: React.ReactNode;
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    /**
     * Render the menu in a portal (body by default) to avoid clipping inside overflow containers.
     */
    usePortal?: boolean;
    portalTarget?: HTMLElement | null;
}

type DropDirection = "down" | "up";

const DEFAULT_OPTION_VALUE: TOptionValue = "unselected";

const normalizeItems = (items: IDropdownItem[]): IDropdownItem[] => {
    return items.map((item) => ({
        ...item,
        value: item.options ? (item.value ?? DEFAULT_OPTION_VALUE) : item.value,
        children: item.children ? normalizeItems(item.children) : undefined,
    }));
};

const mergeItemsWithState = (incoming: IDropdownItem[], previous: IDropdownItem[]): IDropdownItem[] => {
    const previousById = new Map<string, IDropdownItem>();
    const collect = (list: IDropdownItem[]) => {
        list.forEach((entry) => {
            previousById.set(entry.id, entry);
            if (entry.children) collect(entry.children);
        });
    };
    collect(previous);

    const clone = (list: IDropdownItem[]): IDropdownItem[] => {
        return list.map((item) => {
            const previousItem = previousById.get(item.id);
            const children = item.children ? clone(item.children) : undefined;
            const value = item.options ? (item.value ?? previousItem?.value ?? DEFAULT_OPTION_VALUE) : item.value;
            return { ...item, value, children };
        });
    };

    return clone(incoming);
};

const nextOptionValue = (current?: TOptionValue): TOptionValue => {
    return current === "selected" ? "unselected" : "selected";
};

const toggleItemValue = (item: IDropdownItem): IDropdownItem => {
    if (!item.options) return item;
    return { ...item, value: nextOptionValue(item.value) };
};

const findItemById = (items: IDropdownItem[], id: string): IDropdownItem | undefined => {
    for (const item of items) {
        if (item.id === id) return item;
        if (item.children) {
            const match = findItemById(item.children, id);
            if (match) return match;
        }
    }
    return undefined;
};

const updateItemInTree = (
    items: IDropdownItem[],
    id: string,
): { items: IDropdownItem[]; updatedItem?: IDropdownItem; found: boolean } => {
    const targetIndex = items.findIndex((entry) => entry.id === id);
    if (targetIndex !== -1) {
        const target = items[targetIndex];
        const toggled = toggleItemValue(target);

        const nextItems = items.map((entry, index) => {
            if (index === targetIndex) {
                return toggled;
            }
            if (target.options === "radio" && entry.options === "radio") {
                return entry.value === DEFAULT_OPTION_VALUE ? entry : { ...entry, value: DEFAULT_OPTION_VALUE };
            }
            return entry;
        });

        return { items: nextItems, updatedItem: toggled, found: true };
    }

    let found = false;
    let updatedItem: IDropdownItem | undefined;

    const nextItems = items.map((entry) => {
        if (found || !entry.children) return entry;
        const childResult = updateItemInTree(entry.children, id);
        if (childResult.found) {
            found = true;
            updatedItem = childResult.updatedItem;
            return { ...entry, children: childResult.items };
        }
        return entry;
    });

    return { items: nextItems, updatedItem, found };
};

export default function Dropdown({
    items,
    itemTranslation,
    maxHeight,
    onChange,
    trigger,
    isOpen: isOpenProp,
    onOpenChange,
    usePortal = false,
    portalTarget,
}: Props) {
    // Controlled vs uncontrolled open state
    const [openInternal, setOpenInternal] = useState(false);
    const isControlled = isOpenProp !== undefined;
    const isOpen = isControlled ? (isOpenProp as boolean) : openInternal;
    const setOpen = (next: boolean) => {
        if (isControlled) onOpenChange?.(next);
        else setOpenInternal(next);
    };

    const wrapperRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const dropdownId = useRef(`userful-dropdown-${Math.random().toString(36).slice(2, 8)}`);

    const [itemsState, setItemsState] = useState<IDropdownItem[]>(() => normalizeItems(items));
    const [selectedItem, setSelectedItem] = useState<IDropdownItem | null>(null);
    const [openPath, setOpenPath] = useState<string[]>([]);

    // Positioning state
    const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
    const [direction, setDirection] = useState<DropDirection>("down");

    // Force a fresh <ul> mount each time we open to avoid stale inline styles
    const [renderKey, setRenderKey] = useState(0);
    useEffect(() => {
        if (isOpen) setRenderKey((k) => k + 1);
        else {
            // clear stale style when closing so next open starts clean
            setMenuStyle({});
            setDirection("down");
        }
    }, [isOpen]);

    useEffect(() => {
        setItemsState((prev) => mergeItemsWithState(items, prev));
    }, [items]);

    // Close on outside click
    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
            if (!isOpen) return;
            const wrapper = wrapperRef.current;
            const menu = menuRef.current;
            const targetNode = e.target as Node;
            const insideWrapper = wrapper?.contains(targetNode);
            const insideMenu = menu?.contains(targetNode);

            // Sub-menus are rendered in portals and are not children of `menu`.
            // Treat any element inside a dropdown list as "inside" so we don't close
            // when interacting with nested items (e.g., when doNotCloseOnClick is true).
            const insideAnyDropdownList =
                targetNode instanceof Element
                    ? targetNode.closest(`.userful-nested-dropdown-list[data-dropdown-id="${dropdownId.current}"]`)
                    : null;

            if (!insideWrapper && !insideMenu && !insideAnyDropdownList) setOpen(false);
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [isOpen, setOpen]);

    const GAP = 8; // gap between trigger and menu
    const VIEW_PAD = 8; // viewport padding
    const BASE_LEFT = 0; // base left relative to wrapper

    const computePosition = () => {
        const wrapper = wrapperRef.current;
        const menuEl = menuRef.current;
        if (!wrapper || !menuEl) return;

        // Measure wrapper and menu
        const rect = wrapper.getBoundingClientRect();

        // Use scrollWidth/scrollHeight to avoid any transient transforms affecting size
        const menuWidth = (menuEl as HTMLElement).scrollWidth || menuEl.offsetWidth;
        const menuHeight = (menuEl as HTMLElement).scrollHeight || menuEl.offsetHeight;

        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Vertical decision
        const spaceBelow = vh - rect.bottom - GAP;
        const spaceAbove = rect.top - GAP;
        const dropDown = spaceBelow >= menuHeight || spaceBelow >= spaceAbove;
        const nextDirection: DropDirection = dropDown ? "down" : "up";

        // Horizontal nudge
        const predictedLeft = rect.left + BASE_LEFT;
        const predictedRight = predictedLeft + menuWidth;
        let shiftX = 0;
        if (predictedLeft < VIEW_PAD) {
            shiftX = VIEW_PAD - predictedLeft; // push right
        } else if (predictedRight > vw - VIEW_PAD) {
            shiftX = vw - VIEW_PAD - predictedRight; // pull left (negative)
        }

        // Clamp height
        let maxH = Math.max(120, Math.floor(dropDown ? spaceBelow : spaceAbove));
        if (maxHeight !== undefined) {
            maxH = Math.min(maxH, maxHeight);
        }

        const nextTop = dropDown
            ? Math.round(rect.bottom + GAP)
            : Math.max(VIEW_PAD, Math.round(rect.top - GAP - Math.min(menuHeight, maxH)));

        const style: CSSProperties = usePortal
            ? {
                  position: "fixed",
                  left: Math.round(rect.left + shiftX),
                  top: nextTop,
                  zIndex: 9999,
                  maxHeight: `${maxH}px`,
                  overflowY: "auto",
              }
            : {
                  position: "absolute",
                  left: BASE_LEFT,
                  transform: `translateX(${Math.round(shiftX)}px)`,
                  zIndex: 9999,
                  ...(dropDown
                      ? { top: `calc(100% + ${GAP}px)`, bottom: undefined }
                      : { bottom: `calc(100% + ${GAP}px)`, top: undefined }),
                  maxHeight: `${maxH}px`,
                  overflowY: "auto",
              };

        setDirection(nextDirection);
        setMenuStyle(style);
    };

    // Recompute when opened and on resize/scroll/content changes
    useLayoutEffect(() => {
        if (!isOpen) return;

        // Wait a frame to ensure layout is ready, then compute; do it twice to be extra-safe
        const id1 = requestAnimationFrame(() => {
            computePosition();
            const id2 = requestAnimationFrame(() => computePosition());
            // store second id on window to clear in cleanup
            (computePosition as any)._raf2 = id2;
        });

        const onWin = () => computePosition();

        const ro = new ResizeObserver(() => computePosition());
        if (wrapperRef.current) ro.observe(wrapperRef.current);
        if (menuRef.current) ro.observe(menuRef.current);

        window.addEventListener("resize", onWin, { passive: true });
        window.addEventListener("scroll", onWin, { passive: true });

        return () => {
            cancelAnimationFrame(id1);
            if ((computePosition as any)._raf2) {
                cancelAnimationFrame((computePosition as any)._raf2);
            }
            ro.disconnect();
            window.removeEventListener("resize", onWin);
            window.removeEventListener("scroll", onWin);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, itemsState.length, maxHeight]);

    const handleTriggerClick = () => {
        const next = !isOpen;
        setOpen(next);
        setOpenPath([]);
    };

    const shouldCloseOnItemClick = (item: IDropdownItem) => item.doNotCloseOnClick !== true;

    const handleItemClick = (item: IDropdownItem) => {
        const { items: nextItems, updatedItem } = updateItemInTree(itemsState, item.id);
        const selected = updatedItem ?? findItemById(nextItems, item.id) ?? item;

        setItemsState(nextItems);
        setSelectedItem(selected);
        onChange?.(selected);

        if (shouldCloseOnItemClick(item)) {
            setOpen(false);
        }
    };

    // Safe default so it appears even if computePosition hasn't run yet
    const fallbackStyle: CSSProperties =
        usePortal && wrapperRef.current
            ? {
                  position: "fixed",
                  left: wrapperRef.current.getBoundingClientRect().left,
                  top: wrapperRef.current.getBoundingClientRect().bottom + GAP,
                  zIndex: 9999,
              }
            : { top: `calc(100% + ${GAP}px)`, left: BASE_LEFT };

    const effectiveStyle: CSSProperties = isOpen ? { ...fallbackStyle, ...menuStyle } : menuStyle;

    const portalTargetEl = usePortal
        ? (portalTarget ?? (typeof document !== "undefined" ? document.body : null))
        : null;

    return (
        <div ref={wrapperRef} className="userful-dropdown-wrapper noselect" data-direction={direction}>
            <div onClick={handleTriggerClick} className="userful-dropdown-trigger-wrapper">
                {trigger}
            </div>

            {isOpen &&
                (portalTargetEl ? (
                    createPortal(
                        <ul
                            key={renderKey}
                            ref={menuRef}
                            className="userful-nested-dropdown-list"
                            style={effectiveStyle}
                            data-direction={direction}
                            role="menu"
                            data-dropdown-id={dropdownId.current}
                        >
                            <NestedDropdown
                                items={itemsState}
                                selectedItem={selectedItem}
                                openPath={openPath}
                                onTogglePath={setOpenPath}
                                onItemClick={handleItemClick}
                                itemTranslation={itemTranslation}
                                zIndex={9999}
                                dropdownId={dropdownId.current}
                            />
                        </ul>,
                        portalTargetEl,
                    )
                ) : (
                    <ul
                        key={renderKey}
                        ref={menuRef}
                        className="userful-nested-dropdown-list"
                        style={effectiveStyle}
                        data-direction={direction}
                        role="menu"
                        data-dropdown-id={dropdownId.current}
                    >
                        <NestedDropdown
                            items={itemsState}
                            selectedItem={selectedItem}
                            openPath={openPath}
                            onTogglePath={setOpenPath}
                            onItemClick={handleItemClick}
                            itemTranslation={itemTranslation}
                            zIndex={9999}
                            dropdownId={dropdownId.current}
                        />
                    </ul>
                ))}
        </div>
    );
}
